/* eslint-disable react-hooks/exhaustive-deps */
import {
  RefObject,
  createRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "next/navigation";
import { useSession } from "next-auth/react";
import TiktokPixel from "tiktok-pixel";
import { useTrack } from "./useTrack";
import {
  firstSearch,
  useFindAllCommunities,
  useFindByLocationV2,
} from "@/api/traveler";
import { useTranslation } from "@/app/i18n/client";
import categoriesJson from "@/components/home/json/categories.json";
import categoriesProdJson from "@/components/home/json/categoriesProd.json";
import cities from "@/components/home/json/cities.json";
import languagesJson from "@/components/home/json/languages.json";
import mobilityTagsJson from "@/components/home/json/mobilityTags.json";
import tagsJson from "@/components/home/json/tags.json";
import FirebaseContext from "@/context/firebaseContext/firebaseContext";
import SearchContext from "@/context/searchContext/searchContext";
import { ActivitySubCategory, FindByLocationV2Params } from "@/model";
import { FirebaseContextType, SearchContextType } from "@/types/components";

export const useActivities = (
  latitude: number,
  longitude: number,
  categoryId: number = -1,
  radius: number = -1,
) => {
  const { data: session } = useSession();
  const INITIAL_DISTANCE = 50000;
  const { searchedDataFromLanding, categoryFromLanding } = useContext(
    SearchContext,
  ) as SearchContextType;
  const searchParms = useSearchParams();
  const imInProd =
    process.env.NEXT_PUBLIC_ENVIRONMENT_AS_DEVELOPMENT === "false";
  const { i18n } = useTranslation();
  const categories = useRef(imInProd ? categoriesProdJson : categoriesJson);
  const tags = useRef(tagsJson);
  const mobilityTags = useRef(mobilityTagsJson);
  const languages = useRef(languagesJson);
  const getDistanceFromProvince = (province: string): number => {
    const city = cities.find(
      (city) => city.province.toLowerCase() === province.toLowerCase(),
    );
    return city?.radius ?? INITIAL_DISTANCE;
  };
  const getPossibleParams = (): FindByLocationV2Params => {
    let values = { lang: i18n.language } as any;
    if (searchParms.get("lat")) {
      values = { ...values, lat: searchParms.get("lat") };
    }
    if (searchParms.get("lon")) {
      values = { ...values, lon: searchParms.get("lon") };
    }
    if (searchParms.get("distance")) {
      values = { ...values, distance: searchParms.get("distance") };
    }
    if (searchParms.get("community")) {
      values = { ...values, community: searchParms.get("community") };
    }
    if (searchParms.get("city")) {
      values = { ...values, city: searchParms.get("city") };
    }

    // if (searchParms.get("date")) {
    //   values = { ...values, date: searchParms.get("date") };
    // }
    // if (searchParms.get("places")) {
    //   values = { ...values, places: searchParms.get("places") };
    // }
    if (searchParms.get("term")) {
      values = { ...values, term: searchParms.get("term") };
      const arrayOfTerms = searchParms.get("term")?.split(" ") ?? [];
      for (let i = 0; i < arrayOfTerms.length; i++) {
        if (arrayOfTerms[i].toString().startsWith("cat")) {
          const categoryId = arrayOfTerms[i].toString().split(":")[1];
          const category = categories.current.find(
            (category) => category.id == parseInt(categoryId),
          );
          if (category) {
            category.selected = true;
          }
        } else if (arrayOfTerms[i].toString().startsWith("tag")) {
          const tagId = arrayOfTerms[i].toString().split(":")[1];
          const tag = tags.current.find((tag) => tag.id == parseInt(tagId));
          if (tag) {
            tag.selected = true;
          }
        } else if (arrayOfTerms[i].toString().startsWith("mob")) {
          const mobilityId = arrayOfTerms[i].toString().split(":")[1];
          const mobility = mobilityTags.current.find(
            (mobility) => mobility.id == parseInt(mobilityId),
          );
          if (mobility) {
            mobility.selected = true;
          }
        }
      }
    }
    if (searchParms.get("minPrice")) {
      values = { ...values, minPrice: searchParms.get("minPrice") };
    }
    if (searchParms.get("maxPrice")) {
      values = { ...values, maxPrice: searchParms.get("maxPrice") };
    }
    if (searchParms.get("hours")) {
      values = { ...values, hours: searchParms.get("hours") };
    }
    if (searchParms.get("languages")) {
      values = { ...values, languages: searchParms.get("languages") };
      let i = 0;
      const arrayOfLanguages = searchParms.get("languages")?.split(",") ?? [];

      while (i < languages.current.length && arrayOfLanguages.length > 0) {
        if (languages.current[i].id == parseInt(arrayOfLanguages[0])) {
          languages.current[i].selected = true;
          arrayOfLanguages.shift();
        } else {
          i++;
        }
      }
    }
    return values;
  };

  const { logAnalyticsEvent } = useContext(
    FirebaseContext,
  ) as FirebaseContextType;

  const valuesState = useRef(
    searchParms.get("veeSearch") ? getPossibleParams()
    : searchParms.get("utm_source") ?
      searchParms.get("id") ?
        ({
          lang: i18n.language,
          distance: INITIAL_DISTANCE,
          community: searchParms.get("id"),
          term:
            searchParms.get("category") ?
              `cat:${searchParms.get("category")}`
            : "",
        } as FindByLocationV2Params)
      : ({
          lang: i18n.language,
          distance: getDistanceFromProvince(
            searchParms.get("province") ?? searchedDataFromLanding.province,
          ),
          lat:
            searchParms.get("latitudeUrl") ?? searchedDataFromLanding.latitude,
          lon:
            searchParms.get("longitudeUrl") ??
            searchedDataFromLanding.longitude,
          term:
            searchParms.get("category") ?
              `cat:${searchParms.get("category")}`
            : "",
        } as FindByLocationV2Params)
    : searchedDataFromLanding ?
      searchedDataFromLanding.id ?
        ({
          lang: i18n.language,
          distance: INITIAL_DISTANCE,
          community: searchedDataFromLanding.id,
          term: categoryFromLanding ? `cat:${categoryFromLanding}` : "",
        } as FindByLocationV2Params)
      : ({
          lang: i18n.language,
          distance: getDistanceFromProvince(searchedDataFromLanding.province),
          lat: searchedDataFromLanding.latitude,
          lon: searchedDataFromLanding.longitude,
          term: categoryFromLanding ? `cat:${categoryFromLanding}` : "",
        } as FindByLocationV2Params)
    : categoryId !== -1 ?
      ({
        lang: i18n.language,
        lat: latitude,
        lon: longitude,
        distance: radius > 0 ? radius : INITIAL_DISTANCE,
        term: categoryId ? `cat:${categoryId}` : "",
      } as FindByLocationV2Params)
    : ({
        lang: i18n.language,
        lat: latitude,
        lon: longitude,
        distance: radius > 0 ? radius : INITIAL_DISTANCE,
      } as FindByLocationV2Params),
  );
  const { trackEvent, TrackCustomEvents } = useTrack();
  const {
    data: activities,
    refetch,
    isLoading: isRefetching,
  } = useFindByLocationV2(valuesState.current, {
    query: {
      refetchOnWindowFocus: false,
      onSuccess: () => {
        trackEvent({
          name: TrackCustomEvents.EVENT_SEARCHED,
          properties: {
            activity_categories: categoryId,
            community: valuesState.current.community,
            term: valuesState.current.term,
          },
        });
        logAnalyticsEvent(TrackCustomEvents.EVENT_SEARCHED_WEB, {
          activity_categories: categoryId,
          community: valuesState.current.community,
          term: valuesState.current.term,
          city: cityFromLanding,
        });
        if (session) {
          firstSearch();
        }
      },
    },
  });

  // const { data: activitiesFromFareharbor, refetch: refetchFareharbor } =
  //   useFindByLocationV2(
  //     { ...valuesState.current, fromFareharbor: true },
  //     {
  //       query: { refetchOnWindowFocus: false },
  //     },
  //   );
  const [cityFromLanding, setCityFromLanding] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    if (searchParms.get("utm_source")) {
      if (searchParms.get("id")) {
        setCityFromLanding(searchParms.get("community") ?? "");
      } else {
        setCityFromLanding(searchParms.get("province") ?? "");
      }
    }
    if (searchedDataFromLanding) {
      if (searchedDataFromLanding.id) {
        setCityFromLanding(searchedDataFromLanding.community);
      } else {
        setCityFromLanding(searchedDataFromLanding.province);
      }
    }
    if (searchParms.get("veeSearch")) {
      if (searchParms.get("city")) {
        setCityFromLanding(searchParms.get("city") ?? "");
      } else {
        setCityFromLanding(searchParms.get("community") ?? "");
      }
    }
  }, []);

  const [locations, setLocations] = useState<
    {
      lat: number;
      lng: number;
      ref: RefObject<any>;
      subcategory: ActivitySubCategory;
      categoryId: number;
    }[]
  >();
  const [filteredActivities, setFilteredActivities] = useState<any>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { data: communities } = useFindAllCommunities({
    query: { refetchOnWindowFocus: false },
  });

  useEffect(() => {
    if (searchParms.get("category") && categories.current) {
      categories.current.forEach((category) => {
        if (category.id === parseInt(searchParms.get("category") ?? "")) {
          category.selected = true;
        }
      });
    }
    if (categoryFromLanding && categories.current) {
      categories.current.forEach((category) => {
        if (category.id === parseInt(categoryFromLanding)) {
          category.selected = true;
        }
      });
    }
    if (categoryId && categories.current) {
      categories.current.forEach((category) => {
        if (category.id === categoryId) {
          category.selected = true;
        }
      });
    }
  }, [
    searchParms.get("category"),
    categories,
    categoryFromLanding,
    categoryId,
  ]);

  useEffect(() => {
    if (
      activities
      //|| activitiesFromFareharbor
    ) {
      classifyActivities();
    }
  }, [
    activities, //activitiesFromFareharbor
  ]);

  const parseTerm = (term: string) => {
    let termArray: string[] = [];
    if (valuesState.current.term) {
      termArray = valuesState.current.term.split(" ");
    }
    let auxTermString = "";
    if (
      term.includes("tag:") ||
      term.includes("mob:") ||
      term.includes("cat:") ||
      term.includes("subcat:")
    ) {
      if (valuesState.current.term) {
        let wasInArray = false;
        //adding the elements to the term array
        for (let i = 0; i < termArray.length; i++) {
          if (termArray[i] !== term) {
            if (auxTermString === "") {
              auxTermString = termArray[i];
            } else {
              auxTermString = auxTermString.concat(" " + termArray[i]);
            }
          } else {
            //deleting element
            wasInArray = true;
          }
        }
        if (!wasInArray) {
          //adding element
          auxTermString = auxTermString.concat(" " + term);
        }
      } else {
        auxTermString = term;
      }
    } else {
      //changing only name
      auxTermString = term;
      for (let i = 1; i < termArray.length; i++) {
        auxTermString = auxTermString.concat(" " + termArray[i]);
      }
    }

    if (auxTermString === "") {
      delete valuesState.current.term;
    } else {
      valuesState.current = {
        ...valuesState.current,
        term: auxTermString,
      };
    }
  };

  const parseSpecialTerm = (term: string) => {
    let termArray: string[] = [];
    if (valuesState.current.term) {
      termArray = valuesState.current.term.split(" ");
    }
    let auxTermString = "";
    for (let i = 0; i < termArray.length; i++) {
      if (
        termArray[i].includes("tag:") ||
        termArray[i].includes("mob:") ||
        termArray[i].includes("cat:") ||
        termArray[i].includes("subcat:")
      ) {
        if (auxTermString === "") {
          auxTermString = termArray[i];
        } else {
          auxTermString = auxTermString.concat(" " + termArray[i]);
        }
      }
    }
    if (auxTermString === "") {
      auxTermString = term;
    } else {
      auxTermString = auxTermString.concat(" " + term);
    }
    if (auxTermString === "") {
      delete valuesState.current.term;
    } else {
      valuesState.current = {
        ...valuesState.current,
        term: auxTermString,
      };
    }
  };

  const classifyActivities = () => {
    let activitiesByCategory: { [key: string]: any } = {};
    let cruiserVisitants: { [key: string]: any } = {};
    if (activities) {
      const locations2: {
        lat: number;
        lng: number;
        ref: RefObject<any>;
        subcategory: ActivitySubCategory;
        categoryId: number;
      }[] = [];
      activities.forEach((activity) => {
        if (activity.latitud && activity.longitud) {
          locations2.push({
            lat: activity.latitud,
            lng: activity.longitud,
            ref: createRef(),
            subcategory: activity.subcategory ?? {},
            categoryId: activity.category.id,
          });
        }
        if (activity.category.name in activitiesByCategory) {
          activitiesByCategory = {
            ...activitiesByCategory,
            [activity.category.name]: [
              ...activitiesByCategory[activity.category.name],
              activity,
            ],
          };
        } else {
          activitiesByCategory = {
            ...activitiesByCategory,
            [activity.category.name]: [activity],
          };
        }
        //Carousel Visitantes de crucero
        const isCruiserVisitant = activity.tags?.some(
          (tag) => tag === "Visitantes de crucero",
        );
        if (isCruiserVisitant) {
          if ("Visitantes de crucero" in cruiserVisitants) {
            cruiserVisitants = {
              ...cruiserVisitants,
              ["Visitantes de crucero"]: [
                ...cruiserVisitants["Visitantes de crucero"],
                activity,
              ],
            };
          } else {
            cruiserVisitants = {
              ["Visitantes de crucero"]: [activity],
              ...cruiserVisitants,
            };
          }
        }
        //Carousel actividades top
        const isATopActivity = activity.tags?.some(
          (tag) => tag === "Actividades Top",
        );
        if (isATopActivity) {
          if ("Actividades top" in activitiesByCategory) {
            activitiesByCategory = {
              ...activitiesByCategory,
              ["Actividades top"]: [
                ...activitiesByCategory["Actividades top"],
                activity,
              ],
            };
          } else {
            activitiesByCategory = {
              ["Actividades top"]: [activity],
              ...activitiesByCategory,
            };
          }
        }

        //Carousel últimas Actividades
        //Se añaden las actividades donde diaActual - updatedAt <= 30
        const date = new Date();
        const date2 = new Date(activity.updatedAt ?? "");
        const diffTime = Math.abs(date.getTime() - date2.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        //check if actual month - updatedMonth <= 1
        const actualMonth = date.getMonth();
        const updatedMonth = date2.getMonth();
        const diffMonths = Math.abs(actualMonth - updatedMonth);
        if (diffDays <= 30 || diffMonths <= 1) {
          if ("Últimas actividades" in activitiesByCategory) {
            activitiesByCategory = {
              ...activitiesByCategory,
              ["Últimas actividades"]: [
                ...activitiesByCategory["Últimas actividades"],
                activity,
              ],
            };
          } else {
            activitiesByCategory = {
              ["Últimas actividades"]: [activity],
              ...activitiesByCategory,
            };
          }
        }
      });
      setLocations(locations2);
    }
    // if (activitiesFromFareharbor) {
    //   activitiesFromFareharbor.forEach((activity) => {
    //     if ("Fareharbor" in activitiesByCategory) {
    //       activitiesByCategory = {
    //         ...activitiesByCategory,
    //         ["Fareharbor"]: [...activitiesByCategory["Fareharbor"], activity],
    //       };
    //     } else {
    //       activitiesByCategory = {
    //         ["Fareharbor"]: [activity],
    //         ...activitiesByCategory,
    //       };
    //     }
    //   });
    // }
    const objectOrder = {
      "Últimas actividades": null,
      "Actividades top": null,
    };
    const addObjectResource = Object.assign(objectOrder, activitiesByCategory);
    for (const key in addObjectResource) {
      if (addObjectResource[key] === null) {
        delete addObjectResource[key];
      }
    }
    if (Object.keys(cruiserVisitants).length > 0) {
      addObjectResource["Visitantes de crucero"] =
        cruiserVisitants["Visitantes de crucero"];
    }

    setFilteredActivities(addObjectResource);
    setIsLoading(false);
  };

  const changeValuesForFetch = () => {
    let urlParams = "?veeSearch=true";
    valuesState.current.lang = i18n.language;
    for (let i = 0; i < Object.keys(valuesState.current).length; i++) {
      if (Object.keys(valuesState.current)[i] != "lang") {
        urlParams +=
          "&" +
          Object.keys(valuesState.current)[i] +
          "=" +
          //@ts-ignore
          valuesState.current[Object.keys(valuesState.current)[i]];
      }
    }
    refetch();
    TiktokPixel.track("Search", {
      search_string: valuesState.current.term,
      community: valuesState.current.community,
      city: cityFromLanding,
    });
    window.history.replaceState(
      null,
      "Vee Sightseeing",
      "/" + i18n.language + "/home" + urlParams,
    );
    //refetchFareharbor();
  };

  return {
    activities,
    filteredActivities,
    isLoading,
    changeValuesForFetch,
    valuesState,
    categories,
    parseTerm,
    parseSpecialTerm,
    communities,
    tags,
    mobilityTags,
    languages,
    locations,
    cityFromLanding,
    isRefetching,
  };
};
